import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import Swal from "sweetalert2";
import {
  LayoutDashboard,
  Home,
  FileCheck2,
  Factory,
  ClipboardList,
  ListChecks,
  Search,
  LogOut,
  ChevronsUpDown,
  ChevronRight,
} from "lucide-react";
import axios from "../../common/axios";
import { useLogout } from "../../common/logout";
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarRail,
} from "../../components/ui/sidebar";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../../components/ui/dropdown-menu";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "../../components/ui/collapsible";

export default function AppSidebar() {
  const navigate = useNavigate();
  const location = useLocation();
  const logout = useLogout();
  const [userData, setUserData] = useState({});

  useEffect(() => {
    const fetchData = async () => {
      try {
        const res = await axios.post(
          "/dpcurrent_user",
          {},
          { withCredentials: true, headers: { "Content-Type": "application/json" } }
        );
        setUserData(res.data);
      } catch (error) {
        console.error("Error fetching user data:", error);
      }
    };
    fetchData();
  }, []);

  // Nav.js와 동일: userData는 {}로 시작하므로 fetch 완료 전까지 shi=false
  const shi = userData.companyType === "삼성중공업";

  const handleLogout = async () => {
    const result = await Swal.fire({
      title: "로그아웃",
      text: "로그아웃 하시겠습니까?",
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "로그아웃",
      cancelButtonText: "취소",
      confirmButtonColor: "#1d4ed8",
      cancelButtonColor: "#6b7280",
    });
    if (!result.isConfirmed) return;
    logout();
  };

  const isActive = (path) => location.pathname === path;

  return (
    <Sidebar collapsible="icon">
      <SidebarHeader className="px-4 py-3 border-b border-sidebar-border">
        <div className="flex items-center gap-2 group-data-[state=collapsed]:justify-center">
          <div className="w-7 h-7 rounded-md bg-[#1d4ed8] flex items-center justify-center text-[11px] font-bold text-white shrink-0">
            DP
          </div>
          <span className="flex-1 font-semibold text-sidebar-foreground group-data-[state=collapsed]:hidden">
            삼성중공업 DP 시스템
          </span>
        </div>
      </SidebarHeader>

      <SidebarContent>
        <SidebarGroup>
          <SidebarGroupContent>
            <SidebarMenu>
              <SidebarMenuItem>
                <SidebarMenuButton
                  isActive={isActive("/home")}
                  tooltip="홈"
                  onClick={() => navigate("/home")}
                >
                  <Home size={16} />
                  <span>홈</span>
                </SidebarMenuButton>
              </SidebarMenuItem>
              <SidebarMenuItem>
                <SidebarMenuButton
                  isActive={isActive("/dashboard")}
                  tooltip="대시보드"
                  onClick={() => navigate("/dashboard")}
                >
                  <LayoutDashboard size={16} />
                  <span>대시보드</span>
                </SidebarMenuButton>
              </SidebarMenuItem>
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>

        <Collapsible defaultOpen className="group/collapsible">
          <SidebarGroup>
            <SidebarGroupLabel asChild>
              <CollapsibleTrigger className="flex items-center gap-2">
                <FileCheck2 size={14} className="shrink-0" />
                <span className="group-data-[state=collapsed]:hidden flex-1 text-left">승인도 관리</span>
                <ChevronRight
                  size={14}
                  className="shrink-0 transition-transform group-data-[state=collapsed]:hidden group-data-[state=open]/collapsible:rotate-90"
                />
              </CollapsibleTrigger>
            </SidebarGroupLabel>
            <CollapsibleContent>
              <SidebarGroupContent>
                <SidebarMenu>
                  {shi && (
                    <SidebarMenuItem>
                      <SidebarMenuButton
                        isActive={isActive("/adrawing")}
                        tooltip="승인도 계획 관리"
                        onClick={() => navigate("/adrawing")}
                      >
                        <ClipboardList size={16} />
                        <span>계획 관리</span>
                      </SidebarMenuButton>
                    </SidebarMenuItem>
                  )}
                  <SidebarMenuItem>
                    <SidebarMenuButton
                      isActive={isActive("/adrawinginput")}
                      tooltip="승인도 실적 입력"
                      onClick={() => navigate("/adrawinginput")}
                    >
                      <ListChecks size={16} />
                      <span>실적 입력</span>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                  {shi && (
                    <SidebarMenuItem>
                      <SidebarMenuButton
                        isActive={isActive("/adrawinglist")}
                        tooltip="승인도 조회"
                        onClick={() => navigate("/adrawinglist")}
                      >
                        <Search size={16} />
                        <span>조회</span>
                      </SidebarMenuButton>
                    </SidebarMenuItem>
                  )}
                </SidebarMenu>
              </SidebarGroupContent>
            </CollapsibleContent>
          </SidebarGroup>
        </Collapsible>

        <Collapsible defaultOpen className="group/collapsible">
          <SidebarGroup>
            <SidebarGroupLabel asChild>
              <CollapsibleTrigger className="flex items-center gap-2">
                <Factory size={14} className="shrink-0" />
                <span className="group-data-[state=collapsed]:hidden flex-1 text-left">제작도 관리</span>
                <ChevronRight
                  size={14}
                  className="shrink-0 transition-transform group-data-[state=collapsed]:hidden group-data-[state=open]/collapsible:rotate-90"
                />
              </CollapsibleTrigger>
            </SidebarGroupLabel>
            <CollapsibleContent>
              <SidebarGroupContent>
                <SidebarMenu>
                  {shi && (
                    <SidebarMenuItem>
                      <SidebarMenuButton
                        isActive={isActive("/mdrawing")}
                        tooltip="제작도 계획 관리"
                        onClick={() => navigate("/mdrawing")}
                      >
                        <ClipboardList size={16} />
                        <span>계획 관리</span>
                      </SidebarMenuButton>
                    </SidebarMenuItem>
                  )}
                  <SidebarMenuItem>
                    <SidebarMenuButton
                      isActive={isActive("/mdrawinginput")}
                      tooltip="제작도 실적 입력"
                      onClick={() => navigate("/mdrawinginput")}
                    >
                      <ListChecks size={16} />
                      <span>실적 입력</span>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                  {shi && (
                    <SidebarMenuItem>
                      <SidebarMenuButton
                        isActive={isActive("/mdrawinglist")}
                        tooltip="제작도 조회"
                        onClick={() => navigate("/mdrawinglist")}
                      >
                        <Search size={16} />
                        <span>조회</span>
                      </SidebarMenuButton>
                    </SidebarMenuItem>
                  )}
                </SidebarMenu>
              </SidebarGroupContent>
            </CollapsibleContent>
          </SidebarGroup>
        </Collapsible>
      </SidebarContent>

      <SidebarFooter className="border-t border-sidebar-border">
        <SidebarMenu>
          <SidebarMenuItem>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <SidebarMenuButton className="flex items-center gap-2 h-auto py-2 group-data-[state=collapsed]:justify-center group-data-[state=collapsed]:px-0">
                  <div className="w-7 h-7 rounded-full bg-blue-100 flex items-center justify-center text-blue-800 text-xs font-bold shrink-0">
                    {(userData.username || userData.userId || "U").toString().charAt(0)}
                  </div>
                  <div className="flex-1 min-w-0 group-data-[state=collapsed]:hidden">
                    <p className="text-sm font-semibold text-sidebar-foreground truncate">
                      {userData.username || userData.userId || "사용자"}
                    </p>
                    <p className="text-xs text-muted-foreground truncate">
                      {userData.companyType || ""}
                    </p>
                  </div>
                  <ChevronsUpDown size={14} className="shrink-0 text-muted-foreground group-data-[state=collapsed]:hidden" />
                </SidebarMenuButton>
              </DropdownMenuTrigger>
              <DropdownMenuContent side="top" align="start" className="w-56">
                <div className="px-3 py-2 space-y-1">
                  <p className="text-sm font-semibold text-foreground">
                    {userData.username || userData.userId || "사용자"}
                  </p>
                  <p className="text-xs text-muted-foreground truncate">
                    {userData.companyType || ""}
                  </p>
                </div>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={handleLogout} className="gap-2 cursor-pointer">
                  <LogOut size={14} />
                  <span>로그아웃</span>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarFooter>
      <SidebarRail />
    </Sidebar>
  );
}
